import React from 'react';
import { CheckCircle2, Terminal } from 'lucide-react';
import { renderInline, imageLayout } from './blogUtils';

function Block({ block }) {
  switch (block.type) {
    case 'heading': {
      const level = block.level === 4 ? 4 : block.level === 3 ? 3 : 2;
      const Tag = `h${level}`;
      const size = { 2: '2rem', 3: '1.5rem', 4: '1.2rem' }[level];
      return (
        <Tag style={{ fontSize: size, fontWeight: 700, color: '#f1f5f9', letterSpacing: '-0.01em', lineHeight: 1.3, margin: '2.75rem 0 1rem' }}>
          {block.text}
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p style={{ fontSize: '1.05rem', lineHeight: 1.85, color: '#cbd5e1', margin: '0 0 1.5rem' }}>
          {renderInline(block.text)}
        </p>
      );
    case 'image':
      if (!block.imageUrl) return null;
      return (
        <figure style={{ margin: '2.5rem 0' }}>
          <div style={{ ...imageLayout(block), borderRadius: '0.75rem', overflow: 'hidden', border: '1px solid rgba(66,71,84,0.3)', background: '#191b23', padding: '0.75rem' }}>
            <img src={block.imageUrl} alt={block.imageAlt || ''} style={{ width: '100%', display: 'block', borderRadius: '0.4rem' }} />
          </div>
          {block.caption && (
            <figcaption style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.9rem', textAlign: 'center', fontStyle: 'italic' }}>
              {renderInline(block.caption)}
            </figcaption>
          )}
        </figure>
      );
    case 'quote':
      return (
        <blockquote style={{ borderLeft: '4px solid #3b82f6', paddingLeft: '1.5rem', margin: '2.5rem 0', fontStyle: 'italic', fontSize: '1.25rem', color: '#f1f5f9', lineHeight: 1.6 }}>
          {renderInline(block.text)}
        </blockquote>
      );
    case 'list': {
      const items = (block.items || []).filter(Boolean);
      if (!items.length) return null;
      if (block.ordered) {
        return (
          <ol style={{ margin: '0 0 1.5rem', paddingLeft: '1.5rem', color: '#cbd5e1', fontSize: '1.05rem', lineHeight: 1.8 }}>
            {items.map((it, i) => <li key={i} style={{ marginBottom: '0.6rem' }}>{renderInline(it)}</li>)}
          </ol>
        );
      }
      return (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
          {items.map((it, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', color: '#cbd5e1', fontSize: '1.05rem', lineHeight: 1.7 }}>
              <CheckCircle2 size={20} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );
    }
    case 'references': {
      const items = (block.items || []).filter(Boolean);
      if (!items.length) return null;
      return (
        <section style={{ marginTop: '3rem', padding: '2rem', background: '#0a0f1e', border: '1px solid rgba(66,71,84,0.3)', borderRadius: '0.75rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#f1f5f9', margin: '0 0 1.25rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <Terminal size={18} style={{ color: '#3b82f6' }} /> References
          </h3>
          <ol style={{ margin: 0, paddingLeft: '1.25rem', color: '#94a3b8', fontSize: '0.9rem', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
          </ol>
        </section>
      );
    }
    case 'divider':
      return <hr style={{ border: 'none', borderTop: '1px solid rgba(51,65,85,0.6)', margin: '2.5rem 0' }} />;
    case 'spacer':
      return <div aria-hidden="true" style={{ height: `${Math.max(8, Math.min(240, block.heightPx || 40))}px` }} />;
    default:
      return null;
  }
}

export default function BlockRenderer({ blocks }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <div>
      {blocks.map((block, i) => <Block key={block.id || i} block={block} />)}
    </div>
  );
}
